import React, { Children, useEffect, useState } from 'react';
import { Box, Typography, ImageList, Grid, TablePagination, Button, styled } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import ReactSelect from 'react-select';
import { DevTool } from 'react-hook-form-devtools';
import { Virtuoso, VirtuosoGrid } from 'react-virtuoso';
import moment from 'moment';
import GalleryGridItem from './Components/GalleryGridItem';
import PhotoDialog from './Components/Lightbox/PhotoDialog';
import {
  fetchSiteDonorsLocations,
  fetchSiteReceivers,
  fetchSiteRescuers,
  fetchSitesIfNeeded,
  fetchSitesPhotos,
} from '../../../actions/sites';
import useIsAdminInSelectedSite from '../../../hooks/useIsAdminInSelectedSite';
import * as authService from '../../../services/auth';
import { Roles } from '../../../models/roles';
import useActiveUser from '../../../hooks/useActiveUser';
import { fetchUnitsIfNeeded } from '../../../actions/units';
import { fetchFoodDescriptionsIfNeeded } from '../../../actions/foodDescriptions';
import useActiveSite from '../../../hooks/useActiveSite';
import { formatDate } from '../../../helpers/formatters';
import { getRescuePickupLocationFullName } from '../../../helpers/RescuesHelper';
import PagePaper from '../../../components/Common/PagePaper';
import DateAndGranularityPicker, {
  dateRangeOptionsList,
} from '../../../components/DateAndGranularityPicker/DateAndGranularityPicker';
import OverlayLoader from '../../../components/OverlayLoader';
import { fetchFoodDonors } from '../../../actions/foodDonors';
import { fetchReceiversIfNeeded } from '../../../actions/receivers';
import { fetchRescuersIfNeeded } from '../../../actions/rescuers';

const StyledVirtuosoGrid = styled(VirtuosoGrid, {
  shouldForwardProp: (prop) => !['multipleSites'].includes(prop),
})(({ theme, multipleSites }) => ({
  width: '100%',
  height: 'calc(100vh - 30px) !important',
  [theme.breakpoints.up('sm')]: {
    height: 'calc(100vh - 70px) !important',
  },
  ...(multipleSites && {
    height: 'calc(100vh - 100px) !important',
    [theme.breakpoints.up('sm')]: {
      height: 'calc(100vh - 140px) !important',
    },
  }),
}));

const StyledImageList = styled(ImageList)({
  display: 'flex !important',
  flexWrap: 'wrap',
  gap: '0 !important',
  gridTemplateColumns: undefined,
});

const MenuList = ({ children, maxHeight }) => (
  <Virtuoso
    totalCount={Children.count(children)}
    itemContent={(index) => children[index]}
    style={{
      height: maxHeight,
    }}
  />
);

const ItemContainer = ({ children }) => (
  <Box
    component="li"
    sx={{
      overflow: 'hidden',
      padding: '0.1rem',
      width: { xs: '50%', sm: '33.33%', lg: '20%' },
      height: { xs: '50vw', sm: '20vw', md: 'clamp(20vw, 220px, 240px)', lg: '12vw' },
      background: '#f5f5f5',
      display: 'flex',
      flex: 'none',
      alignContent: 'stretch',
    }}
  >
    {children}
  </Box>
);

const validationResolver = data => {
  const errors = {};

  return {
    values: Object.keys(errors).length ? {} : data,
    errors,
  };
};

const isSiteActive = site => site.disabled_at === null && site.deleted_at === null;

const sortOptions = [
  { id: 0, label: 'Sort by most recent photos', value: [ ['created_at', 'desc'] ]},
  { id: 1, label: 'Sort by oldest photos', value: [ ['created_at', 'asc'] ]},
]

const RescuePhotoGalleryView = ({ isMobileView }) => {
  const sitesEntities = useSelector(state => state.entities.sites);
  const photosEntities = useSelector(state => state.entities.sites.photos);
  const foodDonorsEntities = useSelector(state => state.entities.foodDonors);
  const foodDonors = Object.values(foodDonorsEntities.byId);
  const sitesFoodDonorsEntities = useSelector(state => state.entities.sites.donorsLocations);
  const sitesFoodDonors = Object.values(sitesFoodDonorsEntities.byId);
  const receiversEntities = useSelector(state => state.entities.receivers);
  const receiversLocations = Object.values(receiversEntities.locations.byId);
  const sitesReceiversEntities = useSelector(state => state.entities.sites.receivers);
  const sitesReceivers = Object.values(sitesReceiversEntities.byId);
  const rescuersEntities = useSelector(state => state.entities.rescuers);
  const rescuers = Object.values(rescuersEntities.byId);
  const sitesRescuersEntities = useSelector(state => state.entities.sites.rescuers);
  const sitesRescuers = Object.values(sitesRescuersEntities.byId);
  const [showPhoto, setShowPhoto] = useState();
  const [dateRange, setDateRange] = useState(dateRangeOptionsList.last7Days.range);
  const [displayPhotos, setDisplayPhotos] = useState(false);
  const [page, setPage] = useState(0);
  const activeUser = useActiveUser();
  const activeSite = useActiveSite();
  const sitesWithSDSCAccess = authService.getUserSitesByRoles(activeUser.role_assignments, [
    Roles.SiteDirector,
    Roles.SiteCoordinator,
  ]);
  const isAdmin = useIsAdminInSelectedSite();
  const sitesList = isAdmin
    ? Object.values(sitesEntities.byId).filter(isSiteActive)
    : Object.values(sitesEntities.byId).filter(site => sitesWithSDSCAccess.includes(site.id) && isSiteActive(site));
  const foodDonorsList = isAdmin ? foodDonors : sitesFoodDonors;
  const receiversList = isAdmin
    ? (receiversLocations || []).map((location) => ({ ...location, location_id: location.id }))
    : sitesReceivers;
  const rescuersList = isAdmin ? rescuers : sitesRescuers;

  const images = Object.values(photosEntities.allIds.map((id) => photosEntities.byId[id]));
  const filteredImages = displayPhotos ? images : [];

  const itemsPerPage = 20;

  const indexOfLastImage = (page + 1) * itemsPerPage;
  const indexOfFirstImage = indexOfLastImage - itemsPerPage;
  const currentImages = filteredImages.slice(indexOfFirstImage, indexOfLastImage);

  const dispatch = useDispatch();
  const { handleSubmit, getValues, control } = useForm({
    defaultValues: {
      sites: [activeSite],
      order_by: sortOptions[0],
      food_donors: [],
      receivers: [],
      rescuers: [],
    },
    validationResolver: validationResolver,
  });

  const handlePrevPhotoChange = () => {
    const prevPhoto = showPhoto - 1;

    if (prevPhoto >= 0) {
      setShowPhoto(prevPhoto);
    }
  };

  const handleNextPhotoChange = () => {
    const nextPhoto = showPhoto + 1;

    if (nextPhoto < filteredImages.length) {
      setShowPhoto(nextPhoto);
    }
  };

  const fetchSelectedSitesPhotos = (sitesIds, orderBy, from, to, foodDonors, receivers, rescuers) =>
    dispatch(fetchSitesPhotos(sitesIds, orderBy, from, to, foodDonors, receivers, rescuers));

  useEffect(() => {
    dispatch(fetchUnitsIfNeeded());
    dispatch(fetchFoodDescriptionsIfNeeded());
    dispatch(fetchSitesIfNeeded());

    if (isAdmin) {
      dispatch(fetchFoodDonors());
      dispatch(fetchReceiversIfNeeded());
      dispatch(fetchRescuersIfNeeded());
    } else {
      dispatch(fetchSiteRescuers(activeSite.id));
      dispatch(fetchSiteReceivers(activeSite.id));
      dispatch(fetchSiteDonorsLocations(activeSite.id));
    }
  }, [dispatch]);

  const onSubmit = (values) => {
    fetchSelectedSitesPhotos(
      (values.sites || [activeSite]).map((site) => site.id),
      values.order_by.value,
      dateRange?.startDate ? moment(dateRange.startDate).format('YYYY-MM-DD') : null,
      dateRange?.endDate ? moment(dateRange.endDate).format('YYYY-MM-DD') : null,
      values.food_donors.map((fd) => fd.id) || null,
      values.receivers.map((receiver) => receiver.location_id) || null,
      values.rescuers.map((rescuer) => rescuer.id) || null,
    );
    setDisplayPhotos(true);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Box pb={isMobileView ? 8 : 0}>
      <PhotoDialog
        disablePrevButton={showPhoto === 0}
        disableNextButton={showPhoto === filteredImages.length - 1}
        fullScreen={isMobileView}
        rescuePhotoData={filteredImages[showPhoto]}
        handlePrevPhoto={handlePrevPhotoChange}
        handleNextPhoto={handleNextPhotoChange}
        open={!isNaN(showPhoto)}
        handleClose={() => setShowPhoto(false)}
      />

      <Box boxShadow={25} p={2} bgcolor="background.paper">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} alignItems="stretch">
            {sitesList.length > 1 && (
              <Grid item xs={12} sm={3}>
                <Controller
                  name="sites"
                  control={control}
                  as={({ value, onChange }) => (
                    <ReactSelect
                      isDisabled={sitesEntities.inflight || photosEntities.inflight}
                      placeholder="Select site(s) or leave blank to show data for all sites"
                      value={value}
                      isMulti
                      isClearable
                      options={sitesList}
                      getOptionValue={option => option.id}
                      getOptionLabel={option => option.name}
                      onChange={option => {
                        const selectedValues = option || [];
                        onChange(selectedValues);
                      }}
                      styles={{
                        menu: provided => ({
                          ...provided,
                          zIndex: 101,
                        }),
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={3}>
              <Controller
                name="food_donors"
                control={control}
                as={({ value, onChange }) => (
                  <ReactSelect
                    isDisabled={foodDonorsEntities.inflight || sitesFoodDonorsEntities.inflight || photosEntities.inflight}
                    placeholder="Select donor(s)"
                    value={value}
                    isMulti
                    isClearable
                    options={foodDonorsList}
                    getOptionValue={option => option.id}
                    getOptionLabel={option => option.name}
                    onChange={option => {
                      const selectedValues = option || [];
                      onChange(selectedValues);
                    }}
                    components={!!foodDonorsList.length ? { MenuList } : {}}
                    styles={{
                      menu: provided => ({
                        ...provided,
                        zIndex: 101,
                      }),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="receivers"
                control={control}
                as={({ value, onChange }) => (
                  <ReactSelect
                    isDisabled={receiversEntities.inflight || sitesReceiversEntities.inflight || photosEntities.inflight}
                    placeholder="Select agency(s)"
                    value={value}
                    isMulti
                    isClearable
                    options={receiversList}
                    getOptionValue={option => option.id}
                    getOptionLabel={option => option.name}
                    onChange={option => {
                      const selectedValues = option || [];
                      onChange(selectedValues);
                    }}
                    components={!!receiversEntities.length ? { MenuList } : {}}
                    styles={{
                      menu: provided => ({
                        ...provided,
                        zIndex: 101,
                      }),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="rescuers"
                control={control}
                as={({ value, onChange }) => (
                  <ReactSelect
                    isDisabled={rescuersEntities.inflight || sitesRescuersEntities.inflight || photosEntities.inflight}
                    placeholder="Select rescuer(s)"
                    value={value}
                    isMulti
                    isClearable
                    options={rescuersList}
                    getOptionValue={option => option.id}
                    getOptionLabel={option => `${option.firstname} ${option?.lastname}`}
                    onChange={option => {
                      const selectedValues = option || [];
                      onChange(selectedValues);
  
                    }}
                    components={!!rescuersList.length ? { MenuList } : {}}
                    styles={{
                      menu: provided => ({
                        ...provided,
                        zIndex: 101,
                      }),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item>
              <DateAndGranularityPicker
                isDisabled={sitesEntities.inflight || photosEntities.inflight}
                showGranularity={false}
                showCustomDateRange
                showAllDateRange
                customDateRangeOptions={{
                  maxDate: moment().toDate(),
                }}
                defaultDateRange={dateRangeOptionsList.last7Days.value}
                onChange={(data) => {
                  setDateRange(data.dateRange);
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="order_by"
                control={control}
                as={({ value, onChange }) => (
                  <ReactSelect
                    isDisabled={sitesEntities.inflight || photosEntities.inflight}
                    placeholder="Sort photos by:"
                    value={value}
                    options={sortOptions}
                    getOptionValue={option => option.id}
                    onChange={option => {
                      onChange(option);
                    }}
                    styles={{
                      menu: provided => ({
                        ...provided,
                        zIndex: 101,
                      }),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs>
              <Box width="100%" textAlign="right">
                <Button variant="contained" color="primary" type="submit">
                  Run
                </Button>
              </Box>
            </Grid>
          </Grid>
          {process.env.NODE_ENV === 'development' && <DevTool control={control} />}
        </form>
      </Box>
      <OverlayLoader isLoading={photosEntities.inflight}>
        {!photosEntities.inflight && images.length === 0 && displayPhotos && (
          <PagePaper>
            <Typography variant="subtitle1" color="textSecondary">
              Please wait until rescuers upload some photos.
            </Typography>
          </PagePaper>
        )}
        {!photosEntities.inflight && images.length === 0 && !displayPhotos && (
          <PagePaper>
            <Typography variant="subtitle1" color="textSecondary">
              Please use "RUN" button to display photos.
            </Typography>
          </PagePaper>
        )}
        {filteredImages.length > 0 && (
          <StyledVirtuosoGrid
            multipleSites={sitesList.length > 1}
            totalCount={currentImages.length}
            components={{ Item: ItemContainer, List: StyledImageList }}
            itemContent={index => {
              const tile = currentImages[index];
              const filteredImagesIndex = filteredImages.findIndex((image) => image.id === tile.id);
              return (
                <GalleryGridItem handleDialogOpen={() => setShowPhoto(filteredImagesIndex)} tile={tile}>
                  <Box 
                    sx={{
                      padding: 1,
                      color: 'common.white',
                      minHeight: 80,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      position: 'absolute',
                      display: 'flex',
                      flexDirection: 'column',
                      left: 0,
                      right: 0,
                      bottom: 0,
                  }}>
                    <Typography variant="body2">
                      <strong>Date:</strong> {tile.rescue ? formatDate(tile.rescue.date.value) : 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Food Donor:</strong>{' '}
                      {tile.rescue
                        ? getRescuePickupLocationFullName({
                            pickup_location_name: tile.rescue.food_donor.pickup_location.name,
                            location: tile.rescue.food_donor.name,
                          })
                        : 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Receiver:</strong> {tile.rescue ? tile.rescue.receiver.name : 'N/A'}
                    </Typography>
                  </Box>
                </GalleryGridItem>
              );
            }}
          />
        )}
      </OverlayLoader>

      <TablePagination
        component="div"
        count={filteredImages.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={itemsPerPage}
        labelRowsPerPage={null}
        rowsPerPageOptions={[]}
      />
    </Box>
  );
};

export default RescuePhotoGalleryView;
